import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";

import {Tracker} from "../../../interfaces/Tracker";
import {SerialNumber} from "../../../interfaces/tracker/SerialNumber";

import ToastService from "../../../service/ToastService";

import apiV2 from "../../../service/api/ApiV2";
import {EntityTypeEnum} from "../../../enums/EntityTypeEnum";
import {NumberPvPanel} from "../../../interfaces/tracker/NumberPvPanel";
import {SetPvPanel} from "../../../interfaces/tracker/SetPvPanel";
import {getErrorMessage} from "../../../service/AuthenticatedApi";
import i18next from "i18next";

interface TrackersState {
  data: Tracker[];
  trackerMaster: Tracker | null;
  loading: boolean;
  error: string | null;
}

const initialState: TrackersState = {
  data: [],
  trackerMaster: null,
  loading: false,
  error: null,
};

export const updateTrackerSerialNumber = createAsyncThunk<
  {response: Response; serialNumber: SerialNumber},
  SerialNumber,
  {rejectValue: string | null}
>("tracker/updateTrackerSerialNumber", async (serialNumber, {rejectWithValue}) => {
  try {
    const response = await apiV2.updateEntityProperty(EntityTypeEnum.TRACKER, serialNumber.trackerId, [
      {name: "serialNumber", value: serialNumber.serialNumber},
    ]);
    return {response, serialNumber: serialNumber};
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, i18next.t("scada:plant.updates.serialNumberError")));
  }
});

export const updateTrackerNumberPvPanel = createAsyncThunk<
  {response: Response; numberPvPanel: NumberPvPanel},
  NumberPvPanel,
  {rejectValue: string | null}
>("tracker/updateTrackerNumberPvPanel", async (numberPvPanel, {rejectWithValue}) => {
  try {
    const response = await apiV2.updateEntityProperty(EntityTypeEnum.TRACKER, numberPvPanel.trackerId, [
      {name: "moduleQuantity", value: numberPvPanel.numberPvPanel},
    ]);
    return {response, numberPvPanel};
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, i18next.t("scada:plant.updates.panelCountError")));
  }
});

export const updateTrackerPvPanel = createAsyncThunk<
  {response: Response; setPvPanel: SetPvPanel; deviceDescription: string},
  {setPvPanel: SetPvPanel; deviceDescription: string},
  {rejectValue: string | null}
>("tracker/updateTrackerPvPanel", async ({setPvPanel, deviceDescription}, {rejectWithValue}) => {
  try {
    const response = await apiV2.updateTrackerPvPanel(setPvPanel);
    return {response, setPvPanel, deviceDescription};
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, i18next.t("scada:plant.updates.panelTypeError")));
  }
});

const getTrackerFromState = (state: TrackersState, trackerId: number): Tracker => {
  const index = state.data.findIndex((x: Tracker) => x.id === trackerId);
  return state.data[index];
};

const trackersSlice = createSlice({
  name: "Trackers",
  initialState,
  reducers: {
    setTrackers: (state, action) => {
      state.data = action.payload;
    },
    setTrackerMaster: (state, action) => {
      state.trackerMaster = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateTrackerSerialNumber.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTrackerSerialNumber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(updateTrackerSerialNumber.fulfilled, (state, action) => {
        state.loading = false;
        const {serialNumber} = action.payload;
        const tracker = getTrackerFromState(state, serialNumber.trackerId);
        tracker.serialNumber = serialNumber.serialNumber;
        ToastService.success(i18next.t("scada:plant.updates.serialNumber"));
      })
      .addCase(updateTrackerNumberPvPanel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTrackerNumberPvPanel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(updateTrackerNumberPvPanel.fulfilled, (state, action) => {
        state.loading = false;
        const {numberPvPanel} = action.payload;
        const tracker = getTrackerFromState(state, numberPvPanel.trackerId);
        tracker.moduleQuantity = numberPvPanel.numberPvPanel;
        ToastService.success(i18next.t("scada:plant.updates.panelCount"));
      })
      .addCase(updateTrackerPvPanel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTrackerPvPanel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(updateTrackerPvPanel.fulfilled, (state, action) => {
        state.loading = false;
        const {setPvPanel, deviceDescription} = action.payload;
        const tracker = getTrackerFromState(state, setPvPanel.tracker);
        tracker.pvPanel = deviceDescription;
        ToastService.success(i18next.t("scada:plant.updates.panelType"));
      });
  },
});

export const {setTrackers, setTrackerMaster} = trackersSlice.actions;

export default trackersSlice.reducer;
